// lint-internal-leaks-ignore-next-line: framework-script — load-bearing visual regression marker
// @visual-diff-render: vite
/**
 * @file Button — a styled wrapper around MUI's Button
 * @version 0.1.2
 */

import React, { forwardRef, type ElementType } from 'react';
import MuiButton, { type ButtonProps as MuiButtonProps } from '@mui/material/Button';

/**
 * Heidrick Button — a styled wrapper around MUI's Button.
 *
 *
 * Visual styling is applied via `heidrickTheme.components.MuiButton` — wrap
 * your app in `<ThemeProvider theme={heidrickTheme}>` for the Heidrick
 * variants, sizes, and tones to apply.
 *
 * Heidrick-specific props are added to MUI's Button via TypeScript module
 * augmentation:
 *
 *   variant: 'primary' | 'secondary' | 'icon'
 *   size:    'sm' | 'md' (Heidrick canonical) — MUI's 'small' | 'medium' | 'large' also work
 *   tone:    'dark' | 'bright' — defaults vary per variant (see CATALOG)
 *   loading: boolean — renders the in-button spinner + prevents activation
 *
 * Variant defaults:
 *   primary    — supports both tones
 *   secondary  — dark (default, for light surfaces) + bright (white-on-dark)
 *   icon       — bright only (no Dark in V1)
 *
 * @example
 * <Button variant="primary" size="md" tone="dark">Click me</Button>
 * <Button variant="secondary" size="sm">Cancel</Button>
 * <Button variant="icon" aria-label="Close" />
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button({ as, component, ...rest }, ref) {
    // MUI's Button overloads discriminate on `component`/`href` in a union
    // that TS can't resolve once `ref` is pinned to HTMLButtonElement here —
    // same escape hatch Link.tsx uses for its own `as` prop, just applied to
    // the whole props bag since casting only the leaf `component` value still
    // leaves an unresolvable overload.
    const muiProps: Record<string, unknown> = { ref, component: as ?? component, ...rest };
    return <MuiButton {...(muiProps as MuiButtonProps)} />;
  },
);

export interface ButtonProps extends MuiButtonProps {
  /** Render the root as a different element/component (e.g. `'a'`, React Router's
   *  `Link`). Forwards to MUI's own `component` prop under the hood — added so
   *  this primitive is named consistently with every other Heidrick primitive,
   *  which use `as` rather than requiring consumers to know MUI's prop name. */
  as?: ElementType;
}
